import React, { useEffect } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import { Button } from '../../components/base/Button';
import { updateSEO, seoData } from '../../utils/seo';

const ProductsPage: React.FC = () => {
  useEffect(() => {
    updateSEO(seoData.products);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative h-96 flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(30, 58, 138, 0.8), rgba(8, 145, 178, 0.8)), url('https://public.readdy.ai/ai/img_res/e1c70aa296bbd4d851f470309b0bcf5f.jpg')`
        }}
      >
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Premium <strong>Air-Dried Cow Hides</strong>
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            Specializing in export-grade hides with rigorous quality standards and professional processing
          </p>
        </div>
      </section>

      {/* Product Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Export-Grade <strong>Quality Standards</strong>
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Henatos Limited specializes in premium air-dried cow hides averaging 6.5kg–12kg per piece, 
                meticulously sorted, graded, and baled for export. Our hides are processed as raw materials 
                for Pomo Ijebu, a popular Nigerian protein delicacy.
              </p>
              <p className="text-lg text-gray-700 mb-8">
                All hides undergo strict quality control measures and are exclusively air-dried without salt, 
                ensuring optimal moisture content and preservation for international shipping standards.
              </p>
              
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">6.5-12kg</div>
                  <div className="text-gray-600">Average Weight Range</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-cyan-600 mb-2">100%</div>
                  <div className="text-gray-600">Air-Dried Process</div>
                </div>
              </div>
              
              <Button href="/buyers" variant="primary">
                Request Quote
              </Button>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=High-quality%20air-dried%20cow%20hides%20displayed%20for%20inspection%2C%20professional%20grading%20process%2C%20clean%20warehouse%20environment%2C%20workers%20examining%20leather%20quality%2C%20premium%20export-grade%20materials%20sorted%20by%20weight%20and%20grade&width=600&height=400&seq=quality1&orientation=landscape"
                alt="Premium air-dried cow hides quality inspection"
                className="rounded-lg shadow-xl object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Product Specifications */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Product <strong>Specifications</strong>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Detailed specifications ensuring consistent quality and compliance with international standards
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-8 shadow-lg">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-scales-3-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-4 text-center">Weight Classification</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex justify-between"><span>Light Grade:</span> <span className="font-medium">6.5-8.5kg</span></li>
                <li className="flex justify-between"><span>Medium Grade:</span> <span className="font-medium">8.5-10.5kg</span></li>
                <li className="flex justify-between"><span>Heavy Grade:</span> <span className="font-medium">10.5-12kg</span></li>
              </ul>
            </div>
            
            <div className="bg-white rounded-lg p-8 shadow-lg">
              <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-drop-line text-2xl text-cyan-600"></i>
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-4 text-center">Moisture Content</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex justify-between"><span>Maximum:</span> <span className="font-medium">12%</span></li>
                <li className="flex justify-between"><span>Optimal Range:</span> <span className="font-medium">8-10%</span></li>
                <li className="flex justify-between"><span>Testing Method:</span> <span className="font-medium">Digital</span></li>
              </ul>
            </div>
            
            <div className="bg-white rounded-lg p-8 shadow-lg">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-4 text-center">Quality Standards</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-center"><i className="ri-check-line text-green-600 mr-2"></i> No salt treatment</li>
                <li className="flex items-center"><i className="ri-check-line text-green-600 mr-2"></i> Air-dried only</li>
                <li className="flex items-center"><i className="ri-check-line text-green-600 mr-2"></i> Damage-free</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Processing & Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Processing & <strong>Services</strong>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive services from sourcing to delivery, ensuring quality at every step
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-search-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-blue-900 mb-2">Structured Sourcing</h3>
                  <p className="text-gray-600">
                    Coordinated sourcing from trusted vendors across Tanzania, Burundi, Zambia, 
                    and Zimbabwe with advance deposits and steady offtake agreements.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-cyan-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-money-dollar-circle-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-blue-900 mb-2">Vendor Financing</h3>
                  <p className="text-gray-600">
                    Financial support and advance payments to suppliers, enabling consistent 
                    supply and building long-term partnerships across our network.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-truck-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-blue-900 mb-2">Export Logistics</h3>
                  <p className="text-gray-600">
                    Complete logistics coordination via Dar es Salaam, Beira, and Durban ports 
                    with 18-25 days transit time to Nigerian ports.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-cyan-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-award-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-blue-900 mb-2">Quality Assurance</h3>
                  <p className="text-gray-600">
                    Rigorous inspection processes ensuring only properly dried, moisture-free 
                    hides are accepted and processed for export.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-graduation-cap-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-blue-900 mb-2">Technical Support</h3>
                  <p className="text-gray-600">
                    Comprehensive training and support for suppliers on proper drying techniques, 
                    storage methods, and quality maintenance standards.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-cyan-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-file-text-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-blue-900 mb-2">Documentation Support</h3>
                  <p className="text-gray-600">
                    Complete documentation assistance for imports through Apapa and Tin Can ports 
                    in Lagos with full regulatory compliance.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quality Process */}
      <section className="py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Quality <strong>Process</strong>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our systematic approach ensures consistent quality from sourcing to delivery
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">1</span>
              </div>
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Sourcing</h3>
              <p className="text-gray-600">Careful selection from trusted vendors across our African network</p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">2</span>
              </div>
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Inspection</h3>
              <p className="text-gray-600">Rigorous quality checks for moisture content and damage assessment</p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">3</span>
              </div>
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Grading</h3>
              <p className="text-gray-600">Professional sorting and grading by weight and quality standards</p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">4</span>
              </div>
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Export</h3>
              <p className="text-gray-600">Secure baling and shipping to Nigerian ports with full documentation</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-cyan-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Ready to Source <strong>Premium Quality Hides?</strong>
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Partner with Henatos Limited for consistent supply of export-grade air-dried cow hides. 
            Contact us for detailed specifications and pricing.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button href="/buyers" variant="secondary" size="lg">
              Request Quote
            </Button>
            <Button href="/suppliers" variant="outline" size="lg">
              Become a Supplier
            </Button>
            <Button href="/contact" variant="primary" size="lg">
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductsPage;
